import { Moment } from "moment";
import BaseModel from "./BaseModel";
import User from "./User";

export class UserGroupContractMember extends BaseModel {
    username: string;
    userId: number;
    // user is usually filled when one UserGroupContract is fetched but not when many are fetched.
    user?: User;
    startDate?: Date;
    endDate?: Date;

    static __dates = ["startDate", "endDate"];
    constructor(data) {
        super(data, UserGroupContractMember.__dates);
        this.username = data.username;
        this.userId = data.userId;
        this.user = data.user;
        this.startDate = this.__get('startDate');
        this.endDate = this.__get('endDate');
    }
}

export class UserGroupContract extends BaseModel {
    id: number;
    externalId?: number;
    userGroupId: number;
    label: string;
    description: string;
    startDate?: Date;
    endDate?: Date;

    // filled by server in query to get one UserGroupContract but not in query to get many.
    members?: Array<UserGroupContractMember>;

    static __dates = ["startDate", "endDate"];

    constructor(data) {
        super(data, UserGroupContract.__dates);

        this.id = data.id;
        this.externalId = data.externalId;
        this.userGroupId = data.userGroupId;
        this.label = data.label;
        this.description = data.description;
        this.members = data.members?.map((m) => new UserGroupContractMember(m));
        this.startDate = this.__get('startDate');
        this.endDate = this.__get('endDate');
    }

    /** Sort list of user group contracts by label */
    static sortUserGroupContractsByLabel(userGroups: UserGroupContract[]) {
        userGroups.sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()));
    }

    /** Sort list of user group contracts by date ASC with null last */
    static sortUserGroupContractsByDate(userGroups: UserGroupContract[]) {
        userGroups.sort((a, b) => {
            const aDate = a.endDate ?? a.startDate ?? null;
            const bDate = b.endDate ?? b.startDate ?? null;
            if (aDate === null) {
                return bDate === null ? 0 : 1;
            }
            if (bDate === null) {
                return -1;
            }
            return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
        });
    }

    /** Sort list of user group members */
    static sortUserGroupContractsMembers(userGroups: UserGroupContract[]) {
        userGroups.forEach((ug) => {
            ug.members?.sort((a, b) => a.username.toLocaleLowerCase().localeCompare(b.username.toLocaleLowerCase()));
        });
    }

    /** Sorter for list of user group contracts that sort by member usernames */
    static memberEmailSort(a: UserGroupContract, b: UserGroupContract) {
        const amem = a.members?.map((m) => m.username).join(' ');
        const bmem = b.members?.map((m) => m.username).join(' ');
        return amem.toLocaleLowerCase().localeCompare(bmem.toLocaleLowerCase());
    }
}

/// Model that describe the data to collect to register a 'Omflytt'
export class UserGroupNewContract extends BaseModel {
    userGroupId: number;
    terminateContractId?: number;
    terminateDate?: Date;
    newContractStartDate: Moment;
    /// List of email addresses
    newContractMembers: String[];
}